import { useMemo } from 'react';
import { Input, Switch, Select, SelectItem, Autocomplete, AutocompleteItem } from '@heroui/react';
import { DEFAULT_PANEL_FINISH, resolvePanelDim, resolveRailLength, railLengthLink } from '@estimator/shared';
import type { MaterialLibrary, ArmFraming, BanquetteRail, BanquetteAdditionalPanel, PanelFinish, Finish, ComponentDims } from '@estimator/shared';
import BanquetteRailRow from '../banquette/BanquetteRailRow';
import BanquetteAdditionalPanelRow from '../banquette/BanquetteAdditionalPanelRow';
import PanelFinishControls from '../shared/PanelFinishControls';
import PanelListSection from '../shared/PanelListSection';
import MaterialClipboardBar from '../shared/MaterialClipboardBar';
import { useRowKeys } from '@/hooks/useRowKeys';
import { usePanelClipboard } from '@/hooks/usePanelClipboard';
import { movePanel } from '@/utils/panel-materials';
import { useFinishes } from '@/api/finishes';

interface ArmFramingConfigProps {
  framing: ArmFraming;
  materials: MaterialLibrary[];
  onChange: (framing: ArmFraming) => void;
  /** Depth panels run front to back along this dimension. */
  depthInches?: number;
  heightInches?: number;
  /** Live and base dimensions, for resolving dynamic panel sizes. */
  dims?: ComponentDims;
}

export default function ArmFramingConfig({
  framing,
  materials,
  onChange,
  depthInches = 0,
  heightInches = 0,
  dims = { width: 0, depth: 0, height: 0, run_axis: 'depth' },
}: ArmFramingConfigProps) {
  const updateField = <K extends keyof ArmFraming>(key: K, value: ArmFraming[K]) => {
    onChange({ ...framing, [key]: value });
  };

  // Declared above the mutators below, which close over them.
  const railKeys = useRowKeys(framing.rails.length);
  const panelKeys = useRowKeys(framing.additional_panels?.length ?? 0);
  const { copyMaterial, pasteToAll, panelCount } = usePanelClipboard(framing, materials, onChange);

  const reorderRails = (from: number, to: number) => {
    railKeys.move(from, to);
    onChange(movePanel(framing, 'rails', from, to));
  };

  const reorderAdditionalPanels = (from: number, to: number) => {
    panelKeys.move(from, to);
    onChange(movePanel(framing, 'additional_panels', from, to));
  };

  const updateRail = (index: number, updates: Partial<BanquetteRail>) => {
    const rails = [...framing.rails];
    rails[index] = { ...rails[index], ...updates };
    onChange({ ...framing, rails });
  };

  const addRail = () => {
    railKeys.append();
    onChange({
      ...framing,
      rails: [...framing.rails, { name: `Panel ${framing.rails.length + 1}`, width: 3, width_link: null, length: null, length_link: null, material_id: null, board_run_flipped: false, finish: DEFAULT_PANEL_FINISH }],
    });
  };

  const removeRail = (index: number) => {
    railKeys.remove(index);
    onChange({ ...framing, rails: framing.rails.filter((_, i) => i !== index) });
  };

  const additionalPanels = framing.additional_panels ?? [];

  const { data: finishes = [] } = useFinishes();
  const activeFinishes = useMemo(() => finishes.filter((f: Finish) => f.active), [finishes]);
  const defaultFinishName =
    finishes.find((f: Finish) => f.id === framing.default_finish_id)?.name ?? null;

  /** Any panel ticked — gates the template-level default dropdown. The arm has
   *  no ribs, so there is no rib_finish to consider. */
  const anyFinished =
    framing.side_panel_finish?.enabled ||
    framing.rails.some((r) => r.finish?.enabled) ||
    additionalPanels.some((p) => p.finish?.enabled);

  const updateSidePanelFinish = (updates: Partial<PanelFinish>) =>
    onChange({ ...framing, side_panel_finish: { ...framing.side_panel_finish, ...updates } });

  const updateAdditionalPanel = (index: number, updates: Partial<BanquetteAdditionalPanel>) => {
    const panels = [...additionalPanels];
    panels[index] = { ...panels[index], ...updates };
    onChange({ ...framing, additional_panels: panels });
  };

  const addAdditionalPanel = () => {
    panelKeys.append();
    onChange({
      ...framing,
      additional_panels: [...additionalPanels, { name: `Panel ${additionalPanels.length + 1}`, width: 3, width_link: null, height: 3, height_link: null, qty_per_section: 1, material_id: null, board_run_flipped: false, finish: DEFAULT_PANEL_FINISH }],
    });
  };

  const removeAdditionalPanel = (index: number) => {
    panelKeys.remove(index);
    onChange({ ...framing, additional_panels: additionalPanels.filter((_, i) => i !== index) });
  };

  const additionalPanelQtyNote = (panel: BanquetteAdditionalPanel) => {
    const w = resolvePanelDim(panel.width, panel.width_link ?? null, dims);
    const h = resolvePanelDim(panel.height, panel.height_link ?? null, dims);
    const totalQty = panel.qty_per_section;
    const sqft = ((w.value * h.value) / 144) * totalQty;
    const linked = panel.width_link || panel.height_link;
    const prefix = linked ? `→ ${w.value.toFixed(2)}×${h.value.toFixed(2)}″ · ` : '';
    return {
      text: `${prefix}${totalQty} pc${totalQty !== 1 ? 's' : ''} · ${sqft.toFixed(1)} sqft`,
      collapsed: w.collapsed || h.collapsed,
    };
  };

  // Depth panels are one piece each, running the full depth.
  const panelQtyNote = (rail: BanquetteRail) => {
    const w = resolvePanelDim(rail.width, rail.width_link ?? null, dims);
    const l = resolveRailLength(rail, dims);
    const sqftTotal = (w.value * l.value) / 144;
    const linked = rail.width_link || railLengthLink(rail, dims);
    const prefix = linked ? `→ ${w.value.toFixed(2)}×${l.value.toFixed(2)}″ · ` : '';
    return {
      text: `${prefix}1 pc · ${sqftTotal.toFixed(1)} sqft`,
      collapsed: w.collapsed || l.collapsed,
    };
  };

  const effectiveWaste =
    (framing.waste_factor ?? 0.10) + (framing.curved ? (framing.curved_waste_factor ?? 0) : 0);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-3">
        <h3 className="text-xs font-semibold" style={{ color: '#e67e22' }}>Framing Configuration</h3>
        {anyFinished && (
          <Select
            aria-label="Default finish profile"
            label="Finish Profile"
            className="w-72"
            size="sm"
            selectedKeys={framing.default_finish_id ? [framing.default_finish_id] : []}
            onSelectionChange={(keys) =>
              updateField('default_finish_id', (Array.from(keys)[0] as string) || null)
            }
            classNames={{ trigger: 'bg-[var(--bg-panel)]' }}
          >
            {activeFinishes.map((f: Finish) => (
              <SelectItem key={f.id}>
                {`${f.name} — $${Number(f.cost_per_sqft ?? 0).toFixed(2)}/SF`}
              </SelectItem>
            ))}
          </Select>
        )}
      </div>

      <MaterialClipboardBar panelCount={panelCount} onPaste={pasteToAll} />

      <div className="grid grid-cols-2 gap-3">
        <div>
          <Autocomplete
            label="Side Panel Material"
            selectedKey={framing.side_panel_material_id ?? undefined}
            onSelectionChange={(key) => updateField('side_panel_material_id', (key as string) || null)}
            size="sm"
            inputProps={{ classNames: { inputWrapper: 'bg-[var(--bg-panel)]' } }}
          >
            {materials.map((m) => (
              <AutocompleteItem key={m.id}>{m.name}</AutocompleteItem>
            ))}
          </Autocomplete>
          <div className="flex items-center gap-2 mt-0.5">
            <span className="text-[10px]" style={{ color: 'var(--text-muted)' }}>
              2 panels (depth x height
              {depthInches > 0 && heightInches > 0 ? ` = ${depthInches}" x ${heightInches}"` : ''})
            </span>
            <span className="text-[10px]" style={{ color: 'var(--text-muted)' }}>Finish</span>
            <PanelFinishControls
              finish={framing.side_panel_finish}
              onChange={updateSidePanelFinish}
              defaultFinishName={defaultFinishName}
            />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3 items-end">
        <Input
          label="Waste Factor"
          type="number"
          value={((framing.waste_factor ?? 0.1) * 100).toString()}
          onValueChange={(v) => updateField('waste_factor', (Number(v) || 0) / 100)}
          size="sm"
          classNames={{ inputWrapper: 'bg-[var(--bg-panel)]' }}
          endContent={<span className="text-[10px]" style={{ color: 'var(--text-muted)' }}>%</span>}
        />
        <div className="flex items-center gap-2 h-10">
          <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>Curved:</span>
          <Switch
            size="sm"
            isSelected={!!framing.curved}
            onValueChange={(checked) => updateField('curved', checked)}
          >
            <span className="text-xs">{framing.curved ? 'Yes' : 'No'}</span>
          </Switch>
        </div>
        <Input
          label="Curved Waste"
          type="number"
          isDisabled={!framing.curved}
          value={((framing.curved_waste_factor ?? 0) * 100).toString()}
          onValueChange={(v) => updateField('curved_waste_factor', (Number(v) || 0) / 100)}
          size="sm"
          classNames={{ inputWrapper: 'bg-[var(--bg-panel)]' }}
          endContent={<span className="text-[10px]" style={{ color: 'var(--text-muted)' }}>%</span>}
        />
      </div>
      {framing.curved && (framing.curved_waste_factor ?? 0) > 0 && (
        <div className="text-[10px] -mt-1" style={{ color: 'var(--text-muted)' }}>
          Effective framing waste: {(effectiveWaste * 100).toFixed(1)}%
        </div>
      )}

      <PanelListSection
        title="Depth Panels"
        subtitle={
          <>Each panel runs the full{depthInches > 0 ? ` ${depthInches}"` : ''} depth — the number is its width.</>
        }
        items={framing.rails}
        itemKeys={railKeys.keys}
        emptyLabel="No panels configured"
        onAdd={addRail}
        onReorder={reorderRails}
        renderRow={(rail, i) => (
          <BanquetteRailRow
            rail={rail}
            materials={materials}
            onChange={(updates) => updateRail(i, updates)}
            onRemove={() => removeRail(i)}
            onCopyMaterial={() => copyMaterial(rail.material_id)}
            dims={dims}
            defaultFinishName={defaultFinishName}
            showFieldLabels
          />
        )}
        renderNote={(rail) =>
          depthInches > 0 ? (
            <span
              className="text-[10px] flex-shrink-0 w-36 text-right"
              style={{ color: panelQtyNote(rail)?.collapsed ? '#e67e22' : 'var(--text-muted)' }}
            >
              {panelQtyNote(rail)?.text}
            </span>
          ) : null
        }
      />

      <PanelListSection
        title="Additional Panels"
        items={additionalPanels}
        itemKeys={panelKeys.keys}
        emptyLabel="No additional panels configured"
        onAdd={addAdditionalPanel}
        onReorder={reorderAdditionalPanels}
        renderRow={(panel, i) => (
          <BanquetteAdditionalPanelRow
            panel={panel}
            materials={materials}
            onChange={(updates) => updateAdditionalPanel(i, updates)}
            onRemove={() => removeAdditionalPanel(i)}
            onCopyMaterial={() => copyMaterial(panel.material_id)}
            defaultFinishName={defaultFinishName}
          />
        )}
        renderNote={(panel) => (
          <span
            className="text-[10px] flex-shrink-0 w-36 text-right"
            style={{ color: additionalPanelQtyNote(panel)?.collapsed ? '#e67e22' : 'var(--text-muted)' }}
          >
            {additionalPanelQtyNote(panel)?.text}
          </span>
        )}
      />
    </div>
  );
}
